import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Heading from '../../../../generic-components/Heading';
import { CloseButton } from '../../../../DesignSystem/LiliCloseButton/CloseButton';
import { UserMessage } from '../../../../DesignSystem/LiliUserMessage/UserMessage';
import {
  ReactComponent as LiliLogo,
  ReactComponent as LiliIcon,
} from '../../../../assets/icons/svg/icon-new-lili-logo.svg';
import { ReactComponent as SwitcheLoaderRight } from '../../../../assets/account-switcher/active-switch-right.svg';
import { ReactComponent as SwitcheLoaderLeft } from '../../../../assets/account-switcher/active-switch-left.svg';
import { AccountantAccountSwitcherMode, ClientData, IClientResponse } from './types';
import { useStore } from '../../../../store';
import { InviteStatus, mutateInviteStatus } from '../../../../pages/AccountantOnboarding/hooks/mutateInviteStatus';
import { ISummaryData } from '../../../../types';
import { useAccountSummaryQuery } from '../../../../hooks/query/useAccountSummaryQuery';
import { AccountantClientsList } from './AccountantClientsList';
import { setToSessionStorage } from '../../../../services/storage.service';
import { ResetSlicesForAccountSwitching } from '../../../../store/service';
import { SWITCHER_STYLES } from '../account-switcher/constants';
import { InitialsCircle } from '../../../../components/InitialsCircle';
import { getVendorNameInitials } from '../../../../helpers/text.helper';
import { PROFILE_STYLES } from '../../constants';
import { VIEWS_MAP } from '../../../../router/constants';
import LiliToast, { LiliToastDefaultConfig } from '../../../../DesignSystem/LiliToast/LiliToast';
import { eventBus } from '../../../../services/eventBus';

export interface IProps {
  mode: AccountantAccountSwitcherMode;
  onClose: () => void;
}

export const AccountantAccountSwitcher = ({ mode, onClose }: IProps) => {
  const { t } = useTranslation();
  const summaryData: ISummaryData = useStore((state) => state.summary);
  const [clients, setClients] = React.useState<ClientData[]>([]);
  const [isSwitching, setIsSwitching] = useState<boolean>(false);
  const resetUserSliceId = useStore((state) => state.resetUserSliceId);
  const { mutateAsync, isLoading } = mutateInviteStatus();
  const currClient = useRef<ClientData | null>(null);
  const navigate = useNavigate();
  const { refetch, isRefetching } = useAccountSummaryQuery(() => {
    if (currClient.current?.clientId) {
      navigate(`/${VIEWS_MAP.GLOBAL_LOADER}`);
      setTimeout(() => {
        navigate(`/${VIEWS_MAP.TRANSACTIONS}`, { replace: true });
        eventBus.emit('closeProfileMenu', '');
        onClose();
      }, 0);
    }
  }, currClient?.current?.clientId || '');

  const buildClientList = () => {
    const invites = summaryData.authorizedUserData?.authorizedUserInvites || [];
    const pendingInvites = summaryData.authorizedUserData?.authorizedUserPendingInvites || [];

    const combinedLists: IClientResponse[] = [...invites, ...pendingInvites];
    const clientsList: ClientData[] = combinedLists.map((client) => ({
      clientBusinessName: client.businessName || `${client.firstName} ${client.lastName}`,
      clientId: client.customerExternalId,
      ssnOrEIN: client.ein || client.ssn || '',
      expireDate: client.expirationDate,
      needAccept: client.status === 'ACTIVE',
    }));
    setClients(clientsList);
  };
  const onAcceptInvitationClick = async (client: ClientData) => {
    const authId = summaryData.authorizedUserData?.externalId;
    if (!authId) {
      return;
    }
    try {
      await mutateAsync({
        authorizedUserId: authId,
        inviteStatus: InviteStatus.COMPLETED,
        customerId: client.clientId,
        isAuthorizedUser: true,
      });
      toast(
        <LiliToast
          id='inviteAccepted'
          text={`Invitation accepted! Access to ${client.clientBusinessName} granted`}
          showCloseButton
        />,
        { ...LiliToastDefaultConfig },
      );
      refetch().then(() => {
        buildClientList();
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleClientSelection = async (client: ClientData) => {
    currClient.current = client;
    /** Update LocalStorage with new customerId */
    setToSessionStorage({ key: 'customerId', value: client.clientId });
    /** Reset logged-in user id */
    resetUserSliceId(client.clientId);
    /** Reset needed slices */
    ResetSlicesForAccountSwitching();
    setIsSwitching(true);
    await refetch();
  };

  useEffect(() => {
    buildClientList();
    setTimeout(() => {
      buildClientList();
    }, 1000);
  }, [summaryData]);

  const clientsToAccept = useMemo(() => clients.filter((client) => client.needAccept), [summaryData, clients]);
  const readyClients = useMemo(() => clients.filter((client) => !client.needAccept), [summaryData, clients]);

  const onboardingModeStyle = 'w-[500px] bg-bw00 flex flex-col overflow-hidden';
  const profileModeStyle = 'w-[480px] rounded-[12px] shadow-light bg-bw00 flex flex-col overflow-hidden';
  const onboardingWrapperModeStyle = 'w-full h-full bg-bw00 flex flex-col items-center justify-center';

  return isSwitching ? (
    <UserMessage
      mainWrapperOverrideClassName={SWITCHER_STYLES.LOADER_MODAL_WRAPPER}
      onClick={() => {}}
      id='swithcerLoader'
    >
      <div className='flex flex-col items-center'>
        <div className='mb-[20px]'>
          <InitialsCircle
            id={currClient.current?.clientId}
            initialsLetters={getVendorNameInitials(currClient.current?.clientBusinessName || '')}
            circleWrapperClassName={SWITCHER_STYLES.LOADER_PROFILE}
            initialsClassName={PROFILE_STYLES.ACCOUNT_SWITCHER_TRIGGER.PROFILES.PROFILE_ITEM_TITLE}
          >
            <div className='absolute inset-0 animate-spin'>
              <SwitcheLoaderRight className={SWITCHER_STYLES.LOADER_ICON_RIGHT} />
              <SwitcheLoaderLeft className={SWITCHER_STYLES.LOADER_ICON_LEFT} />
            </div>
          </InitialsCircle>
        </div>

        <Heading
          title={`${t('ACCOUNT_SWITCHER.LOADING_MESSAGE')} ${currClient.current?.clientBusinessName}...`}
          className={SWITCHER_STYLES.LOADER_TITLE}
        />
        <LiliIcon className={SWITCHER_STYLES.LOADER_LILI_ICON} />
      </div>
    </UserMessage>
  ) : (
    <UserMessage
      onClick={onClose}
      id='AccountSwitcherWrapper'
      contentWrapperOverrideClassName={
        mode === AccountantAccountSwitcherMode.ONBOARDING ? onboardingWrapperModeStyle : ''
      }
    >
      {mode === AccountantAccountSwitcherMode.ONBOARDING && <LiliLogo className='absolute left-0 top-0 m-[36px]' />}
      <div className={mode === AccountantAccountSwitcherMode.PROFILE ? profileModeStyle : onboardingModeStyle}>
        {mode === AccountantAccountSwitcherMode.PROFILE ? (
          <div className='flex items-center justify-between py-[19px] px-[24px] border-b-bw03 border-b '>
            <Heading
              title={t('ACCOUNTANT_ACCOUNT_SWITCHER.PROFILE_TITLE')}
              className='text-[18px] text-bw08 font-metricRMedium'
            />
            <CloseButton id='close-button' onClick={onClose} />
          </div>
        ) : (
          <Heading
            title={t('ACCOUNTANT_ACCOUNT_SWITCHER.ONBOARDING_TITLE')}
            className='mb-[14px] text-[24px] text-bw08 font-metricRMedium text-center'
          />
        )}

        {!!clientsToAccept.length && (
          <div>
            {mode === AccountantAccountSwitcherMode.ONBOARDING && (
              <Heading
                title={t('ACCOUNTANT_ACCOUNT_SWITCHER.ONBOARDING_INVITATIONS_TITLE')}
                className='ml-[8px] mb-[8px] my-[2px] text-[14px] text-bw06 font-metricR'
              />
            )}
            <AccountantClientsList
              clients={clientsToAccept}
              onAcceptInvitationClick={onAcceptInvitationClick}
              isLoading={isLoading || isRefetching}
              mode={mode}
              type='PENDING'
            />
          </div>
        )}

        <div>
          {mode === AccountantAccountSwitcherMode.ONBOARDING && (
            <Heading
              title={t('ACCOUNTANT_ACCOUNT_SWITCHER.PROFILE_TITLE')}
              className='ml-[8px] mb-[8px] my-[2px] text-[14px] text-bw06 font-metricR'
            />
          )}
          <AccountantClientsList
            onClientSelection={handleClientSelection}
            clients={readyClients}
            isLoading={isLoading || isRefetching}
            mode={mode}
          />
        </div>
      </div>
    </UserMessage>
  );
};
